/**
 * FeedbackModal — in-app feedback v1 (gg-app CONTRACT.md, PR #322).
 *
 * Category (Bug/Idea/Question) + free-text body, with an opt-in "store my
 * contact info" checkbox that reveals an editable, prefilled email field.
 * Identity rides ONLY inside `contact` — it is per-submission consent, so the
 * request never includes it unless the checkbox is checked at submit time
 * (verified by the "typed an email, then unticked" regression test).
 *
 * COPY RULE (pinned): never use the word "anonymous" anywhere in this
 * component — the privacy line below states the retention/identity behavior
 * directly instead ("we don't store your identity unless you tick the box").
 *
 * Deliberately NOT offline-capable: submit is disabled while offline (see
 * useSendFeedback.ts for why this isn't routed through the offline mutation
 * queues — a feedback body may carry an email address, and those queues are
 * IndexedDB). Every other diary/service-log flow in this app is offline-
 * first; this one is the intentional exception.
 */

import { Button } from '@/components/Button';
import { Field, inputStyle, selectStyle } from '@/components/Field';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import { useModalFocus } from '@/hooks/useModalFocus';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useSendFeedback } from '@/hooks/useSendFeedback';
import { ApiError } from '@/lib/api';
import type { FeedbackCategory, FeedbackRequest } from '@/types/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, X } from 'lucide-react';
import { type CSSProperties, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

export interface FeedbackModalProps {
  onClose: () => void;
}

// Mirrors gg-api's MAX_FEEDBACK_BODY_BYTES exactly (crates/gg-api/src/feedback.rs) —
// the total JSON request-body cap, not a separate per-field limit.
const MAX_FEEDBACK_PAYLOAD_BYTES = 6 * 1024;

/** Builds exactly what would be sent over the wire, to measure its real byte size. */
function buildPayload(
  category: string,
  body: string,
  contactOptIn: boolean,
  contactEmail: string,
): Record<string, unknown> {
  return {
    category,
    body: body.trim(),
    ...(contactOptIn ? { contact: { replyTo: contactEmail.trim() } } : {}),
  };
}

function payloadByteSize(
  category: string,
  body: string,
  contactOptIn: boolean,
  contactEmail: string,
): number {
  return new TextEncoder().encode(
    JSON.stringify(buildPayload(category, body, contactOptIn, contactEmail)),
  ).length;
}

const FeedbackFormSchema = z
  .object({
    category: z.string().min(1, 'Choose a category.'),
    body: z.string().trim().min(1, 'Tell us what happened.'),
    contactOptIn: z.boolean(),
    contactEmail: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.contactOptIn && data.contactEmail.trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['contactEmail'],
        message: 'Enter an email, or untick the box above.',
      });
    }
    const bytes = payloadByteSize(data.category, data.body, data.contactOptIn, data.contactEmail);
    if (bytes > MAX_FEEDBACK_PAYLOAD_BYTES) {
      ctx.addIssue({
        code: 'custom',
        path: ['body'],
        message: 'That’s too long — trim it down and try again.',
      });
    }
  });

type FeedbackFormValues = z.infer<typeof FeedbackFormSchema>;

function feedbackErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Too many submissions, try again in a minute.';
    if (err.status === 400) return err.message || 'Please check your feedback and try again.';
  }
  return 'Something went wrong. Try again.';
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.88)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: SPACE.md,
};

const dialogStyle: CSSProperties = {
  background: T.bg,
  borderRadius: RADIUS.lg,
  width: '100%',
  maxWidth: 560,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${SPACE.md}px ${SPACE.md}px ${SPACE.sm}px`,
  borderBottom: `1px solid ${T.border}`,
  flexShrink: 0,
};

const closeBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: T.text,
  borderRadius: RADIUS.md,
  padding: SPACE.xs,
  minWidth: 44,
  minHeight: 44,
  transition: `background ${TRANSITION.fast}`,
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: SPACE.md,
  display: 'flex',
  flexDirection: 'column',
  gap: SPACE.md,
  minHeight: 0,
};

const footerStyle: CSSProperties = {
  display: 'flex',
  gap: SPACE.sm,
  padding: `${SPACE.sm}px ${SPACE.md}px ${SPACE.md}px`,
  borderTop: `1px solid ${T.border}`,
  flexShrink: 0,
  justifyContent: 'flex-end',
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 120,
};

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const { dialogRef, initialRef: closeRef } = useModalFocus<HTMLDivElement, HTMLButtonElement>(
    true,
  );
  const { user } = useAuth();
  const online = useOnlineStatus();
  const mutation = useSendFeedback();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FeedbackFormValues>({
    resolver: zodResolver(FeedbackFormSchema),
    defaultValues: {
      category: '',
      body: '',
      contactOptIn: false,
      contactEmail: user?.email ?? '',
    },
  });

  const category = useWatch({ control, name: 'category' });
  const body = useWatch({ control, name: 'body' });
  const contactOptIn = useWatch({ control, name: 'contactOptIn' });
  const contactEmail = useWatch({ control, name: 'contactEmail' });

  const bytesUsed = payloadByteSize(category, body, contactOptIn, contactEmail);
  const overCap = bytesUsed > MAX_FEEDBACK_PAYLOAD_BYTES;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Success is transient — auto-close shortly after, mirroring a toast
  // without needing a global toast primitive (none exists in this codebase).
  useEffect(() => {
    if (!mutation.isSuccess) return;
    const timer = setTimeout(onClose, 1500);
    return () => clearTimeout(timer);
  }, [mutation.isSuccess, onClose]);

  const onSubmit = handleSubmit((data) => {
    const payload: FeedbackRequest = {
      category: data.category as FeedbackCategory,
      body: data.body.trim(),
      ...(data.contactOptIn ? { contact: { replyTo: data.contactEmail.trim() } } : {}),
    };
    mutation.mutate(payload);
  });

  const canSubmit = online && !mutation.isPending && !mutation.isSuccess;

  return (
    <div style={overlayStyle} role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Send feedback"
        style={dialogStyle}
      >
        <div style={headerStyle}>
          <p
            style={{ margin: 0, fontSize: 15, fontWeight: 600, fontFamily: F.body, color: T.text }}
          >
            Send feedback
          </p>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close feedback dialog"
            style={closeBtnStyle}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {mutation.isSuccess ? (
          <div style={bodyStyle}>
            <div
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACE.sm,
                fontSize: 14,
                fontFamily: F.body,
                color: T.text,
              }}
            >
              <Check size={18} color={T.green} aria-hidden="true" />
              Thanks — we read every one.
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)}>
            <div style={bodyStyle}>
              <Field label="Category" error={errors.category?.message}>
                <select style={selectStyle} {...register('category')}>
                  <option value="" disabled>
                    Choose a category
                  </option>
                  <option value="bug">Bug</option>
                  <option value="idea">Idea</option>
                  <option value="question">Question</option>
                </select>
              </Field>

              <div>
                <Field label="What's on your mind?" error={errors.body?.message}>
                  <textarea
                    style={textareaStyle}
                    {...register('body')}
                    aria-describedby="feedback-byte-count"
                  />
                </Field>
                <p
                  id="feedback-byte-count"
                  style={{
                    margin: `${SPACE.xs}px 0 0`,
                    fontSize: 12,
                    fontFamily: F.body,
                    color: overCap ? T.red : T.textMuted,
                  }}
                >
                  {(bytesUsed / 1024).toFixed(1)} KB of 6 KB
                </p>
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.sm,
                  fontSize: 13,
                  fontFamily: F.body,
                  color: T.text,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                <input
                  type="checkbox"
                  {...register('contactOptIn')}
                  style={{ width: 18, height: 18, accentColor: T.accent }}
                />
                Store my contact info so we can reply
              </label>

              {contactOptIn ? (
                <Field label="Email" error={errors.contactEmail?.message}>
                  <input type="email" style={inputStyle} {...register('contactEmail')} />
                </Field>
              ) : null}

              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  fontFamily: F.body,
                  color: T.textMuted,
                  lineHeight: 1.55,
                }}
              >
                Your feedback is kept for 7 days, then deleted. We don&rsquo;t store your identity
                unless you tick the box above.
              </p>

              {!online ? (
                <p style={{ margin: 0, fontSize: 13, fontFamily: F.body, color: T.orange }}>
                  You&rsquo;re offline — feedback can&rsquo;t be sent right now.
                </p>
              ) : null}

              {mutation.isError ? (
                <p
                  role="alert"
                  style={{ margin: 0, fontSize: 13, fontFamily: F.body, color: T.red }}
                >
                  {feedbackErrorMessage(mutation.error)}
                </p>
              ) : null}
            </div>

            <div style={footerStyle}>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={!canSubmit || overCap}
                loading={mutation.isPending}
              >
                Send feedback
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
